import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { BackgroundRequestServices } from "@/services/bedrock/BackgroundRequestServices";
import { BackgroundRequest } from "@/services/bedrock/dtos/BackgroundAsset";
import { designKeys } from "@/services/bedrock/queryKeys";

const POLL_INTERVAL_MS = 5_000;
const TERMINAL: BackgroundRequest["status"][] = ["done", "failed", "canceled"];

export const isRequestPending = (request: BackgroundRequest) => !TERMINAL.includes(request.status);

export interface UseBackgroundRequestsOptions {
  scope?: "mine" | "all";
  coachId?: number;
  designTemplateId?: number;
  batchKeys?: string[];
  /** Off for viewers who can't create requests (the list is forbidden to them). */
  enabled?: boolean;
}

/** The account's background requests, polled every 5 s while any of them is still queued or running. */
export function useBackgroundRequests({
  scope,
  coachId,
  designTemplateId,
  batchKeys,
  enabled = true,
}: UseBackgroundRequestsOptions) {
  const params = { scope: scope ?? "mine", coachId, designTemplateId, batchKey: batchKeys };
  const { data } = useQuery({
    queryKey: designKeys.backgrounds.requests(params),
    queryFn: () => BackgroundRequestServices.list(params),
    refetchInterval: query => (query.state.data?.content.some(isRequestPending) ? POLL_INTERVAL_MS : false),
    enabled,
  });

  const requests = data?.content ?? [];
  const pending = requests.filter(isRequestPending);
  const waits = pending.map(r => r.estimatedWaitSeconds).filter((s): s is number => s != null);
  const estimatedWaitSeconds = waits.length > 0 ? Math.max(...waits) : null;

  // Same rule as the server's batch completion: every request finished, none produced a picture, one failed.
  const failedBatch = useCallback(
    (batchKey: string) => {
      const batch = requests.filter(r => r.batchKey === batchKey);
      return (
        batch.length > 0 &&
        batch.every(r => !isRequestPending(r)) &&
        !batch.some(r => r.status === "done") &&
        batch.some(r => r.status === "failed")
      );
    },
    [requests]
  );

  return { requests, pending, estimatedWaitSeconds, failedBatch };
}
